import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, KeyRound, Trash2, Power, Pencil, X, ShieldCheck, ShieldAlert, User as UserIcon, RotateCcw, Users, UserCheck, Copy, Check, Mail, AlertTriangle, BellRing } from "lucide-react";
import api from "@/lib/api";
import { KpiTile } from "@/pages/cockpit/ui";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

export const SECTIONS = [
  { key: "leads", label: "Anfragen" },
  { key: "tasks", label: "Aufgaben" },
  { key: "support", label: "Support" },
  { key: "performance", label: "Besuche & Performance" },
  { key: "invoices", label: "Rechnungen" },
  { key: "next-steps", label: "Nächste Schritte" },
];
const ROLE_LABEL = { client: "Kunden-Admin", client_staff: "Mitarbeiter" };

const fmtDay = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return "—"; }
};

const inp = "w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#A3E635]";

export default function UserManager({ clientId, accent = "#A3E635", showStats = false }) {
  const { t } = useI18n();
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [modal, setModal] = useState(null); // {mode:'add'} | {mode:'edit', user} | {mode:'pw', user}

  const load = () => api.get(`/clients/${clientId}/users`).then((r) => setUsers(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const toggleActive = async (u) => {
    try { await api.put(`/users/${u.id}`, { active: !(u.active !== false) }); toast.success(t("Aktualisiert")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Fehler")); }
  };
  const del = async (u) => {
    if (!window.confirm(t("Benutzer {email} wirklich löschen?").replace("{email}", u.email))) return;
    try { await api.delete(`/users/${u.id}`); toast.success(t("Benutzer gelöscht")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Fehler")); }
  };
  const reset2fa = async (u) => {
    if (!window.confirm(t("2FA für {email} zurücksetzen? Der Nutzer richtet die Authenticator-App beim nächsten Login neu ein.").replace("{email}", u.email))) return;
    try { await api.post(`/users/${u.id}/reset-2fa`); toast.success(t("2FA zurückgesetzt")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Fehler")); }
  };
  const remind2fa = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/remind-2fa`);
      if (data.email_sent) toast.success(t("2FA-Erinnerung an {email} gesendet.").replace("{email}", u.email));
      else toast.error(t("Erinnerung konnte gerade nicht zugestellt werden."));
    } catch (e) { toast.error(e.response?.data?.detail || t("Erinnerung konnte nicht gesendet werden.")); }
  };
  const [remindingAll, setRemindingAll] = useState(false);
  const remindAll2fa = async () => {
    setRemindingAll(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/remind-all-2fa`);
      if (data.sent > 0) {
        const extra = data.skipped_recent ? " " + t("{s} kürzlich bereits erinnert.").replace("{s}", data.skipped_recent) : "";
        toast.success(t("{n} Erinnerung(en) gesendet.").replace("{n}", data.sent) + extra);
      } else if (data.failed > 0) {
        toast.error(t("Erinnerungen konnten gerade nicht zugestellt werden."));
      } else {
        toast.info(t("Keine offenen Erinnerungen — alle wurden bereits kürzlich erinnert."));
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Aktion fehlgeschlagen.")); }
    finally { setRemindingAll(false); }
  };

  if (!users) return <div className="text-sm text-gray-500 py-6">{t("Lade Benutzer…")}</div>;

  const total = users.length;
  const active = users.filter((u) => u.active !== false).length;
  const admins = users.filter((u) => u.role === "client").length;
  const staff = users.filter((u) => u.role === "client_staff").length;
  const withoutTwofa = users.filter((u) => !u.totp_enabled && u.id !== me?.id).length;

  return (
    <div className="space-y-4" data-testid="user-manager">
      {showStats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4" data-testid="cteam-stats">
          <KpiTile icon={Users} tone="lime" accent={accent} value={total} label={t("Benutzer gesamt")} sub={t("in Ihrer Firma")} testid="cteam-kpi-total" />
          <KpiTile icon={UserCheck} tone="green" accent={accent} value={active} label={t("Aktive")} sub={t("mit Zugang")} testid="cteam-kpi-active" />
          <KpiTile icon={ShieldCheck} tone="blue" accent={accent} value={admins} label={t("Kunden-Admins")} sub={t("volle Rechte")} testid="cteam-kpi-admins" />
          <KpiTile icon={UserIcon} tone="purple" accent={accent} value={staff} label={t("Mitarbeiter")} sub={t("eingeschränkt")} testid="cteam-kpi-staff" />
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-bold">{t("Benutzer & Zugriffe")}</h3>
          <p className="text-xs text-gray-500">{t("Kunden-Admin = volle eigene Firma · Mitarbeiter = nur freigegebene Bereiche")}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {withoutTwofa > 0 && (
            <button onClick={remindAll2fa} disabled={remindingAll} data-testid="remind-all-2fa-btn"
              className="font-semibold py-2.5 px-4 rounded-xl text-sm flex items-center gap-2 border border-white/15 bg-white/5 hover:bg-white/10 text-gray-200 disabled:opacity-60 transition-colors">
              <BellRing className="w-4 h-4" style={{ color: accent }} /> {remindingAll ? t("Sende …") : `${t("Alle ohne 2FA erinnern")} (${withoutTwofa})`}
            </button>
          )}
          <button onClick={() => setModal({ mode: "add" })} data-testid="user-add-btn"
            className="text-black font-bold py-2.5 px-4 rounded-xl text-sm flex items-center gap-2 active:scale-95 transition-transform" style={{ background: accent }}>
            <UserPlus className="w-4 h-4" /> {t("Benutzer hinzufügen")}
          </button>
        </div>
      </div>

      <div className="bg-[#121217] border border-white/10 rounded-2xl divide-y divide-white/5">
        {users.length === 0 && (
          <div className="flex flex-col items-center justify-center text-center py-12 px-4" data-testid="user-empty">
            <span className="w-12 h-12 rounded-2xl flex items-center justify-center bg-white/5 border border-white/10 mb-3"><Users className="w-6 h-6 text-gray-500" /></span>
            <div className="text-sm font-semibold text-gray-300">{t("Noch keine Benutzer.")}</div>
            <p className="text-xs text-gray-500 mt-1 max-w-[300px]">{t("Legen Sie Mitarbeiter Ihrer Firma an und steuern Sie deren Zugriffe.")}</p>
          </div>
        )}
        {users.map((u) => {
          const active = u.active !== false;
          return (
            <div key={u.id} className="p-4 flex items-center gap-3 flex-wrap hover:bg-white/[0.02] transition-colors" data-testid={`user-row-${u.id}`}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}1A`, color: accent }}>
                {u.role === "client" ? <ShieldCheck className="w-5 h-5" /> : <UserIcon className="w-5 h-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm flex items-center gap-2">
                  {u.name}
                  {!active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-300">{t("Inaktiv")}</span>}
                </div>
                <div className="text-xs text-gray-500 truncate">{u.email}</div>
              </div>
              <div className="hidden sm:flex flex-col items-end mr-1 gap-1">
                <div className="flex items-center gap-1.5">
                  <span title={u.totp_enabled ? t("2FA aktiv") : t("2FA nicht aktiv")} data-testid={`user-2fa-${u.id}`}
                    className="inline-flex items-center justify-center w-6 h-6 rounded-md shrink-0"
                    style={{ background: u.totp_enabled ? `${accent}1A` : "rgba(251,191,36,0.12)", color: u.totp_enabled ? accent : "#FBBF24" }}>
                    {u.totp_enabled ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
                  </span>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-gray-200">{t(ROLE_LABEL[u.role] || u.role)}</span>
                </div>
                <span className="text-[10px] text-gray-500" data-testid={`user-lastlogin-${u.id}`}>
                  {u.last_login ? t("Zuletzt aktiv: {d}").replace("{d}", fmtDay(u.last_login)) : t("Noch nie angemeldet")}
                </span>
                {u.role === "client_staff" && (
                  <span className="text-[10px] text-gray-500 max-w-[220px] text-right">
                    {(u.allowed_sections || []).map((s) => t(SECTIONS.find((x) => x.key === s)?.label || s)).join(", ") || t("keine Bereiche")}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 w-full sm:w-auto justify-around sm:justify-end mt-1 sm:mt-0 pt-2 sm:pt-0 border-t border-white/5 sm:border-0">
                <button onClick={() => setModal({ mode: "edit", user: u })} data-testid={`user-edit-${u.id}`} title={t("Bearbeiten")} className="p-3 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 active:scale-95 transition"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => setModal({ mode: "pw", user: u })} data-testid={`user-pw-${u.id}`} title={t("Passwort setzen")} className="p-3 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 active:scale-95 transition"><KeyRound className="w-4 h-4" /></button>
                <button onClick={() => reset2fa(u)} data-testid={`user-reset2fa-${u.id}`} title={t("2FA zurücksetzen")} className="p-3 rounded-lg text-gray-400 hover:text-amber-300 hover:bg-white/5 active:scale-95 transition"><RotateCcw className="w-4 h-4" /></button>
                {!u.totp_enabled && u.id !== me?.id && (
                  <button onClick={() => remind2fa(u)} data-testid={`user-remind2fa-${u.id}`} title={t("2FA-Erinnerung senden")} className="p-3 rounded-lg text-gray-400 hover:text-[#A3E635] hover:bg-white/5 active:scale-95 transition"><BellRing className="w-4 h-4" /></button>
                )}
                <button onClick={() => toggleActive(u)} data-testid={`user-active-${u.id}`} title={active ? t("Deaktivieren") : t("Aktivieren")} className={`p-3 rounded-lg hover:bg-white/5 active:scale-95 transition ${active ? "text-[#A3E635]" : "text-gray-600"}`}><Power className="w-4 h-4" /></button>
                <button onClick={() => del(u)} data-testid={`user-del-${u.id}`} title={t("Löschen")} className="p-3 rounded-lg text-gray-400 hover:text-red-400 hover:bg-white/5 active:scale-95 transition"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
      </div>

      {modal?.mode === "add" && <UserFormModal clientId={clientId} accent={accent} onClose={() => setModal(null)} onReload={load} onSaved={() => { setModal(null); load(); }} />}
      {modal?.mode === "edit" && <UserFormModal clientId={clientId} accent={accent} user={modal.user} onClose={() => setModal(null)} onReload={load} onSaved={() => { setModal(null); load(); }} />}
      {modal?.mode === "pw" && <PasswordModal user={modal.user} accent={accent} onClose={() => setModal(null)} />}
    </div>
  );
}

function UserFormModal({ clientId, user, accent, onClose, onSaved, onReload }) {
  const { t } = useI18n();
  const isEdit = !!user;
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(user?.role || "client_staff");
  const [sections, setSections] = useState(user?.allowed_sections || ["tasks", "support"]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // Erfolgs-Sequenz nach dem Anlegen
  const [copied, setCopied] = useState(false);
  const [lang, setLang] = useState("de"); // Sprache der Zugangs-Mail

  const toggleSection = (k) => setSections((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const copyPw = async () => { try { await navigator.clipboard.writeText(result.temp_password); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ } };

  const submit = async () => {
    if (!name.trim()) return toast.error(t("Name fehlt"));
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/users/${user.id}`, { name, role, allowed_sections: role === "client_staff" ? sections : [] });
        toast.success(t("Benutzer aktualisiert"));
        onSaved();
      } else {
        if (!email.trim()) { setSaving(false); return toast.error(t("E-Mail fehlt")); }
        const { data } = await api.post(`/clients/${clientId}/users`, {
          name, email, role, password: password || null, language: lang,
          allowed_sections: role === "client_staff" ? sections : [],
        });
        onReload && onReload();
        setResult({ name, email, temp_password: data.temp_password, email_sent: !!data.email_sent });
      }
    } catch (e) { toast.error(e.response?.data?.detail || t("Fehler beim Speichern")); }
    finally { setSaving(false); }
  };

  if (result) {
    return (
      <Overlay onClose={onClose} title={t("Benutzer angelegt")} testid="user-created">
        <div className="text-center">
          <span className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: `${accent}1A`, color: accent }}><UserCheck className="w-7 h-7" /></span>
          <div className="font-bold text-lg">{result.name}</div>
          <div className="text-sm text-gray-500 break-all">{result.email}</div>
        </div>

        <div className={`mt-4 flex items-start gap-2.5 rounded-xl border p-3 text-sm ${result.email_sent ? "border-[#A3E635]/30 bg-[#A3E635]/10" : "border-amber-400/30 bg-amber-400/10"}`} data-testid="user-created-mail">
          {result.email_sent ? <Mail className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accent }} /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-300" />}
          <div>
            {result.email_sent ? (
              <span className="text-gray-200">{t("Zugangs-Mail an {email} gesendet.").replace("{email}", result.email)}</span>
            ) : (
              <span className="text-amber-100/90">{t("Zugangs-Mail konnte gerade nicht zugestellt werden. Bitte teilen Sie die Zugangsdaten unten direkt mit.")}</span>
            )}
          </div>
        </div>

        {result.temp_password ? (
          <div className="mt-3">
            <label className="text-xs text-gray-400 mb-1 block">{t("Start-Passwort")}</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#A3E635] font-mono break-all" data-testid="user-created-pw">{result.temp_password}</code>
              <button onClick={copyPw} data-testid="user-created-copy" title={t("Passwort kopieren")} className="shrink-0 p-3 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300 border border-white/10 transition-colors">{copied ? <Check className="w-4 h-4 text-[#A3E635]" /> : <Copy className="w-4 h-4" />}</button>
            </div>
            <p className="text-[11px] text-gray-500 mt-1.5">{t("Der Benutzer muss beim ersten Login ein eigenes Passwort festlegen.")}</p>
          </div>
        ) : (
          <p className="text-sm text-gray-400 mt-3">{t("Das von Ihnen vergebene Passwort gilt für den ersten Login — danach legt der Benutzer sein eigenes fest.")}</p>
        )}

        <div className="flex justify-end mt-5">
          <button onClick={onClose} data-testid="user-created-done" className="text-black font-bold px-5 py-2 rounded-lg text-sm active:scale-95 transition-transform" style={{ background: accent }}>{t("Fertig")}</button>
        </div>
      </Overlay>
    );
  }

  return (
    <Overlay onClose={onClose} title={isEdit ? t("Benutzer bearbeiten") : t("Benutzer hinzufügen")} testid="user-form-modal">
      <div className="space-y-3">
        <div><label className="text-xs text-gray-400 mb-1 block">{t("Name")}</label>
          <input className={inp} data-testid="uf-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("Vor- und Nachname")} /></div>
        {!isEdit && (
          <div><label className="text-xs text-gray-400 mb-1 block">{t("E-Mail")}</label>
            <input className={inp} data-testid="uf-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@firma.ch" /></div>
        )}
        {!isEdit && (
          <div><label className="text-xs text-gray-400 mb-1 block">{t("Passwort")} <span className="text-gray-600">{t("(leer = willkommen2026)")}</span></label>
            <input className={inp} data-testid="uf-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("optional")} /></div>
        )}
        {!isEdit && (
          <div><label className="text-xs text-gray-400 mb-1 block">{t("Sprache der Zugangs-Mail")}</label>
            <select className={inp} data-testid="uf-language" value={lang} onChange={(e) => setLang(e.target.value)}>
              <option value="de">Deutsch</option>
              <option value="fr">Français</option>
              <option value="it">Italiano</option>
              <option value="en">English</option>
            </select></div>
        )}
        <div><label className="text-xs text-gray-400 mb-1 block">{t("Rolle")}</label>
          <select className={inp} data-testid="uf-role" value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="client">{t("Kunden-Admin (volle eigene Firma)")}</option>
            <option value="client_staff">{t("Mitarbeiter (eingeschränkt)")}</option>
          </select></div>
        {role === "client_staff" && (
          <div>
            <label className="text-xs text-gray-400 mb-1.5 block">{t("Freigegebene Bereiche")}</label>
            <div className="grid grid-cols-2 gap-2">
              {SECTIONS.map((s) => (
                <label key={s.key} data-testid={`uf-sec-${s.key}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${sections.includes(s.key) ? "border-[#A3E635]/50 bg-[#A3E635]/10" : "border-white/10"}`}>
                  <input type="checkbox" className="accent-[#A3E635]" checked={sections.includes(s.key)} onChange={() => toggleSection(s.key)} />
                  {t(s.label)}
                </label>
              ))}
            </div>
            <p className="text-[11px] text-gray-500 mt-1.5">{t("Überblick ist immer sichtbar.")}</p>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{t("Abbrechen")}</button>
        <button onClick={submit} disabled={saving} data-testid="uf-save" className="text-black font-bold px-4 py-2 rounded-lg text-sm disabled:opacity-60" style={{ background: accent }}>
          {isEdit ? t("Speichern") : (saving ? t("Wird angelegt…") : t("Anlegen"))}
        </button>
      </div>
    </Overlay>
  );
}

function PasswordModal({ user, accent, onClose }) {
  const { t } = useI18n();
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if ((pw || "").length < 6) return toast.error(t("Mindestens 6 Zeichen"));
    setSaving(true);
    try { await api.post(`/users/${user.id}/password`, { password: pw }); toast.success(t("Passwort gesetzt")); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Fehler")); } finally { setSaving(false); }
  };
  return (
    <Overlay onClose={onClose} title={t("Passwort setzen — {name}").replace("{name}", user.name)} testid="user-pw-modal">
      <label className="text-xs text-gray-400 mb-1 block">{t("Neues Passwort")}</label>
      <input className={inp} data-testid="pw-input" value={pw} onChange={(e) => setPw(e.target.value)} placeholder={t("Mindestens 6 Zeichen")} />
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{t("Abbrechen")}</button>
        <button onClick={submit} disabled={saving} data-testid="pw-save" className="text-black font-bold px-4 py-2 rounded-lg text-sm disabled:opacity-60" style={{ background: accent }}>{t("Setzen")}</button>
      </div>
    </Overlay>
  );
}

function Overlay({ children, onClose, title, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#121217] border border-white/10 rounded-2xl p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-bold">{title}</h4>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
